import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, onValue, set, get, remove, onDisconnect } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import './ActivePulse.css';

const HEARTBEAT_INTERVAL = 5000; // Update presence every 5 seconds
const PRESENCE_TIMEOUT = 10000; // Consider offline if no update in 10 seconds

function ActivePulse() {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();
  const audioRef = useRef(null);
  const heartbeatRef = useRef(null);
  const presenceRefStore = useRef(null);
  const isProcessingPulse = useRef(false);

  const [isPartnerOnline, setIsPartnerOnline] = useState(false);
  const [partnerName, setPartnerName] = useState('Partner');
  const [partnerUid, setPartnerUid] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [showScreenPulse, setShowScreenPulse] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Play pulse sound
  const playPulseSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.log('Audio play failed:', err));
    }
  }, []);

  // Set up presence heartbeat
  useEffect(() => {
    if (!currentUser || !userProfile) return;

    const presenceRef = ref(database, `presence/${currentUser.uid}`);
    presenceRefStore.current = presenceRef;

    const updatePresence = () => {
      set(presenceRef, {
        online: true,
        lastSeen: Date.now(),
        userName: userProfile.name || 'User'
      }).catch(err => console.log('Presence update failed:', err));
    };

    // Set initial presence
    updatePresence();

    // Set up heartbeat interval
    heartbeatRef.current = setInterval(updatePresence, HEARTBEAT_INTERVAL);

    // Set up onDisconnect to mark offline when connection is lost
    const connectedRef = ref(database, '.info/connected');
    const unsubscribeConnected = onValue(connectedRef, (snap) => {
      if (snap.val() === true) {
        onDisconnect(presenceRef).set({
          online: false,
          lastSeen: Date.now(),
          userName: userProfile.name || 'User'
        });
      }
    });

    // Cleanup
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      // Mark as offline when component unmounts
      set(presenceRef, {
        online: false,
        lastSeen: Date.now(),
        userName: userProfile.name || 'User'
      }).catch(() => {});
      unsubscribeConnected();
    };
  }, [currentUser, userProfile]);

  // Find and listen to partner's presence
  useEffect(() => {
    if (!currentUser || !userProfile) return;

    let unsubscribePresence = null;
    let unsubscribePulse = null;

    // Get all users to find partner
    const usersRef = ref(database, 'users');
    get(usersRef).then((snapshot) => {
      if (snapshot.exists()) {
        const users = snapshot.val();
        // Find partner (user that isn't current user and isn't admin)
        // Must have 'email' property to be a valid user profile (not just timezone data)
        const partnerEntry = Object.entries(users).find(([uid, userData]) => {
          // Skip if this is the current user
          if (uid === currentUser.uid) return false;
          // Skip if not a valid user profile (must have email)
          if (!userData || typeof userData !== 'object' || !userData.email) return false;
          // Skip admin users
          if (userData.role === 'admin' || userData.name?.toLowerCase() === 'admin') return false;
          return true;
        });

        if (partnerEntry) {
          const [pUid, partnerData] = partnerEntry;
          setPartnerUid(pUid);
          setPartnerName(partnerData.name || 'Partner');

          // Listen to partner's presence
          const partnerPresenceRef = ref(database, `presence/${pUid}`);
          unsubscribePresence = onValue(partnerPresenceRef, (presenceSnapshot) => {
            if (presenceSnapshot.exists()) {
              const data = presenceSnapshot.val();
              // Check if presence is fresh (within timeout)
              const isFresh = Date.now() - data.lastSeen < PRESENCE_TIMEOUT;
              setIsPartnerOnline(data.online && isFresh);
            } else {
              setIsPartnerOnline(false);
            }
          });

          // Listen for incoming pulses
          const pulseRef = ref(database, `pulses/${currentUser.uid}`);
          unsubscribePulse = onValue(pulseRef, (pulseSnapshot) => {
            if (pulseSnapshot.exists()) {
              const pulse = pulseSnapshot.val();
              // Prevent double-processing with local flag
              if (!pulse.consumed && !isProcessingPulse.current) {
                isProcessingPulse.current = true;
                // Mark as consumed immediately in Firebase
                set(ref(database, `pulses/${currentUser.uid}/consumed`), true);
                // Play sound
                playPulseSound();
                // Show screen pulse
                setShowScreenPulse(true);
                // Clear after animation (matches 7s CSS animation)
                setTimeout(() => {
                  setShowScreenPulse(false);
                  isProcessingPulse.current = false;
                  // Remove the pulse from database
                  remove(ref(database, `pulses/${currentUser.uid}`));
                }, 7000);
              }
            }
          });
        }
      }
    }).catch(err => console.error('Error fetching users:', err));

    // Cleanup function
    return () => {
      if (unsubscribePresence) unsubscribePresence();
      if (unsubscribePulse) unsubscribePulse();
    };
  }, [currentUser, userProfile, playPulseSound]);

  // Handle sending pulse to partner
  const handleSendPulse = async () => {
    if (!isPartnerOnline || !partnerUid || isSending) return;

    // Visual feedback
    setIsSending(true);

    try {
      // Write to partner's pulse inbox
      const pulseRef = ref(database, `pulses/${partnerUid}`);
      await set(pulseRef, {
        senderName: userProfile?.name || 'Your partner',
        timestamp: Date.now(),
        consumed: false
      });
    } catch (err) {
      console.error('Pulse send failed:', err);
    }

    // Reset sending state after animation
    setTimeout(() => setIsSending(false), 600);
  };

  // Don't render if not logged in
  if (!currentUser || !userProfile) {
    return null;
  }

  // Don't show for admin
  if (userProfile.role === 'admin' || userProfile.name?.toLowerCase() === 'admin') {
    return null;
  }

  const isOnHomePage = location.pathname === '/home';

  return (
    <>
      {/* Screen pulse overlay - shows on ALL pages when receiving pulse */}
      {showScreenPulse && (
        <div className="screen-pulse-overlay" key={Date.now()}>
          <div className="pulse-inner-glow" />
        </div>
      )}

      {/* Main pulse indicator - only shows on home page */}
      {isOnHomePage && (
      <div className="active-pulse-container">
        <motion.div
          className={`pulse-indicator ${isPartnerOnline ? 'active' : ''} ${isSending ? 'sending' : ''}`}
          onClick={handleSendPulse}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          whileHover={isPartnerOnline ? { scale: 1.15 } : {}}
          whileTap={isPartnerOnline ? { scale: 0.95 } : {}}
        >
          <svg viewBox="0 0 24 24" className="pulse-heart-icon">
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              fill="currentColor"
            />
          </svg>
        </motion.div>

        {/* Custom tooltip */}
        <AnimatePresence>
          {showTooltip && isPartnerOnline && (
            <motion.div
              className="pulse-tooltip"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              transition={{ duration: 0.15 }}
            >
              Click to send a pulse to {partnerName}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Partner online message */}
        <AnimatePresence>
          {isPartnerOnline && (
            <motion.div
              className="partner-home-message"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {partnerName} is home too!
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      )}

      {/* Audio element for pulse sound */}
      <audio ref={audioRef} preload="auto">
        <source src="/sounds/pulse.mp3" type="audio/mpeg" />
        <source src="/sounds/crystal.mp3" type="audio/mpeg" />
      </audio>
    </>
  );
}

export default ActivePulse;
